/**
 * choice.v1 cizici — iceriğin ~%83'u bu mekanikte (legacy A1/A2/A5/A7/A8/A9/B1).
 *
 * Sozlesme (docs/EcupRunner/03 + ChoiceV1Handler.cs):
 *   - item.content : StripAnswerKey'den gecmis icerik — isCorrect BURADA YOK.
 *   - cevap        : onAnswerChange({ selectedOptionIds: [...] }); secim yoksa
 *                    null (allowEmpty ise bos dizi) -> "ileri" butonu kapali kalir.
 *   - item.answer  : geri gidildiginde eski secim burdan doldurulur.
 *
 * Sunumlar (LEGACY ECUP akisiyla ayni):
 *   - metin sikli / gorsel sikli (layout.variant)
 *   - tekil / coklu secim (selection.mode)
 *   - sesli soru (prompt.audioUrl): prompt + ayrac + ortada "Sesi oynat" -> dalga
 *     -> ses bitince siklar (tekrar dinle YOK)
 *   - goster-sakla (reveal.steps): "aklinda tut" + "Resimleri goster" -> resimler
 *     sureli acilir, kaybolur -> soru + siklar
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { MechanicProps } from "./types";
import { sesiGuvenliOynat } from "./audioSafety";
import type { ChoiceContent, ChoiceOption, ChoiceScene, ChoiceShape } from "./content";

/** Metindeki "\n"leri satir sonuna cevirir (tek satirli metinler degismeden gecer). */
function ml(s?: string) {
  if (!s || !s.includes("\n")) return s;
  const lines = s.split("\n");
  return lines.map((ln, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {ln}
    </Fragment>
  ));
}

/**
 * Koddan SVG cizilen sekil (slayta sadik). Dikkat izgarasinda lacivert konturlu;
 * bellek duzeninde (flat) duz dolgulu — slayttaki gibi kontursuz.
 */
function ShapeSVG({ shape, flat }: { shape: ChoiceShape; flat?: boolean }) {
  const { type, color, dot } = shape;
  const stroke = flat ? "none" : "#12308a"; // slayttaki lacivert kontur (grid); flat=kontursuz
  const sw = flat ? 0 : 2.5;
  return (
    <svg viewBox="0 0 44 44" className="shape-svg" aria-hidden="true">
      {type === "arrow" && (
        // cift govdeli ok (slayttaki gibi); sola bakan celdirici icin aynala.
        <g
          fill="none"
          stroke={color}
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
          transform={shape.dir === "left" ? "translate(44,0) scale(-1,1)" : undefined}
        >
          <line x1="5" y1="17" x2="30" y2="17" />
          <line x1="5" y1="27" x2="30" y2="27" />
          <polyline points="27,8 41,22 27,36" />
        </g>
      )}
      {type === "circle" && (
        <circle cx="22" cy="22" r="17" fill={color} stroke={stroke} strokeWidth={sw} />
      )}
      {type === "square" && (
        <rect x="5" y="5" width="34" height="34" rx="2" fill={color} stroke={stroke} strokeWidth={sw} />
      )}
      {type === "triangle" && (
        <polygon points="22,4 39,37 5,37" fill={color} stroke={stroke} strokeWidth={sw}
          strokeLinejoin="round" />
      )}
      {type === "star" && (
        <polygon
          points="22,2 26.7,15.5 41,15.8 29.6,24.5 33.8,38.2 22,30 10.2,38.2 14.4,24.5 3,15.8 17.3,15.5"
          fill={color} stroke={stroke} strokeWidth={sw} strokeLinejoin="round"
        />
      )}
      {dot && <circle cx="22" cy={type === "triangle" ? 28 : 22} r="4" fill={dot} />}
    </svg>
  );
}

/** sceneMatch: cakisan iki kare uzerine slot'a yerlesen sekiller (koddan cizilir). */
const SCENE_SLOTS: Record<string, [number, number]> = {
  tr: [170, 46], // sag-ust karenin sag-ustu (elmas evi)
  tc: [108, 47], // sag-ust karenin ust-ortasi (celdirici elmas)
  center: [105, 105], // cakisma bolgesi ortasi
  "bl-left": [52, 165], // sol-alt karenin sol-alti
  "bl-center": [100, 167], // sol-alt karenin alt-ortasi
};
function SceneSVG({ scene }: { scene: ChoiceScene }) {
  const items = scene.items ?? [];
  return (
    <svg viewBox="0 0 210 210" className="scene-svg" aria-hidden="true">
      {/* iki cakisan kare (sabit arkaplan) */}
      <rect x="70" y="12" width="128" height="128" fill="none" stroke="#111" strokeWidth="4" />
      <rect x="12" y="70" width="128" height="128" fill="none" stroke="#111" strokeWidth="4" />
      {items.map((it, i) => {
        const [cx, cy] = SCENE_SLOTS[it.slot] ?? [105, 105];
        if (it.type === "circle")
          return <circle key={i} cx={cx} cy={cy} r="21" fill={it.color} />;
        if (it.type === "diamond")
          return (
            <polygon
              key={i}
              points={`${cx},${cy - 20} ${cx + 20},${cy} ${cx},${cy + 20} ${cx - 20},${cy}`}
              fill={it.color}
            />
          );
        if (it.type === "triangle")
          return (
            <polygon
              key={i}
              points={`${cx},${cy - 21} ${cx - 22},${cy + 19} ${cx + 22},${cy + 19}`}
              fill={it.color}
            />
          );
        return null;
      })}
    </svg>
  );
}

/** iconCount: sik icinde tekrar eden pati/el ikonu (koddan SVG cizilir). */
function CountIcon({ kind }: { kind: "paw" | "hand" }) {
  if (kind === "paw") {
    return (
      <svg viewBox="0 0 32 32" width={30} height={30} aria-hidden="true">
        <g fill="#8a5a2b">
          <ellipse cx="16" cy="22" rx="8" ry="6.5" />
          <ellipse cx="7" cy="13" rx="3" ry="4" />
          <ellipse cx="13" cy="9" rx="3" ry="4.2" />
          <ellipse cx="19" cy="9" rx="3" ry="4.2" />
          <ellipse cx="25" cy="13" rx="3" ry="4" />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 32 32" width={30} height={30} aria-hidden="true">
      <g fill="#f7c9a0" stroke="#c98a56" strokeWidth={0.9} strokeLinejoin="round" strokeLinecap="round">
        <rect x="9" y="6" width="3" height="13" rx="1.5" />
        <rect x="13" y="3" width="3" height="16" rx="1.5" />
        <rect x="17" y="3" width="3" height="16" rx="1.5" />
        <rect x="21" y="6" width="3" height="13" rx="1.5" />
        <rect x="8.5" y="14" width="16" height="13" rx="5" />
        <rect x="4" y="17" width="8" height="3.2" rx="1.6" transform="rotate(-38 8 18)" />
      </g>
    </svg>
  );
}

export function ChoiceItem({ item, onAnswerChange }: MechanicProps) {
  const content = item.content as unknown as ChoiceContent;
  const prompt = content.prompt ?? {};
  const options: ChoiceOption[] = content.options ?? [];
  const multiple = content.selection?.mode === "multiple";
  const allowEmpty = content.selection?.allowEmpty === true;
  const isImages = (content.layout?.variant ?? "imageOptions") === "imageOptions";
  // Cumle sikli (sosyal-duygusal, >16 karakter) esit genislik yan yana.
  const eqText = !isImages && options.some((o) => (o.text ?? "").length > 16);
  const reveal = content.reveal ?? null;

  const initial = (item.answer?.selectedOptionIds as string[] | undefined) ?? [];
  const [selected, setSelected] = useState<string[]>(initial);

  const hasReveal = !!(reveal && reveal.steps && reveal.steps.length > 0);
  const hasApplicator = !!content.applicator;
  // uygulayici gecidi -> reveal (aklinda tut) -> show (resimler) -> answer (soru)
  const [phase, setPhase] = useState<"applicator" | "intro" | "show" | "answer">(
    hasApplicator ? "applicator" : hasReveal ? "intro" : "answer",
  );
  const [stepIx, setStepIx] = useState(0);
  const [audioState, setAudioState] = useState<"idle" | "playing" | "done">("idle");
  // colorSequence sürükleme durumu (renk kartını sıra şeridine sürükle)
  const [csDrag, setCsDrag] = useState<{ id: string; x: number; y: number; x0: number; y0: number } | null>(null);

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    if (selected.length > 0) cb.current({ selectedOptionIds: selected });
    else cb.current(allowEmpty ? { selectedOptionIds: [] } : null);
  }, [selected, allowEmpty]);

  // reveal "show": adimlari sureli goster, sonra gizle -> cevap.
  useEffect(() => {
    if (phase !== "show" || !hasReveal) return;
    const steps = reveal!.steps!;
    // Goster-sakla varsayilan gosterim suresi: 10 sn (icerik durationMs verebilir).
    const ms = steps[stepIx]?.durationMs ?? 10000;
    const t = setTimeout(() => {
      if (stepIx + 1 < steps.length) setStepIx(stepIx + 1);
      else setPhase("answer");
    }, ms);
    return () => clearTimeout(t);
  }, [phase, stepIx, hasReveal, reveal]);

  function toggle(id: string) {
    if (multiple) {
      setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    } else {
      setSelected([id]);
    }
  }

  // colorSequence sürükleme: renk kartını sıra şeridine bırak = ekle, havuza = çıkar
  useEffect(() => {
    if (!csDrag) return;
    const move = (e: PointerEvent) =>
      setCsDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    const up = (e: PointerEvent) => {
      const id = csDrag.id;
      // Dokunma (neredeyse hic kaymadan birakma): havuzdaki renk siraya eklenir,
      // siradaki renk havuza geri doner. Hatali yerlestirilen renk boylece geri alinir.
      if (Math.hypot(e.clientX - csDrag.x0, e.clientY - csDrag.y0) < 8) {
        setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
        setCsDrag(null);
        return;
      }
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const strip = el?.closest("[data-cs-strip]");
      if (strip) setSelected((s) => (s.includes(id) ? s : [...s, id]));
      // seridin disina birakilan renk havuza doner
      else setSelected((s) => s.filter((x) => x !== id));
      setCsDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csDrag]);

  const audio = useRef<HTMLAudioElement | null>(null);
  // audioButton: ses İÇERİĞİ GİZLEMEDEN eşlik eder (grid/renkler görünür kalır).
  // audioButton yoksa klasik "gate" akışı (ses bitince şıklar açılır).
  const hasAudioButton = !!content.layout?.audioButton;
  const audioGated = !!prompt.audioUrl && !hasAudioButton;
  const showOptions = !audioGated || audioState === "done";
  const playAudio = () => {
    const a = audio.current;
    if (a) {
      a.currentTime = 0;
      void a.play();
    }
  };
  // audioButton'lu sorularda üstte çalışan "Sesi oynat" (ses yoksa devre dışı iskelet).
  const audioButtonEl = hasAudioButton ? (
    <div className="center-action" style={{ margin: "8px 0 14px" }}>
      {prompt.audioUrl && audioState === "playing" ? (
        <div className="waveform" aria-label="ses çalıyor">
          <span /><span /><span /><span /><span /><span /><span />
        </div>
      ) : (
        <button
          type="button"
          className="big-action-btn"
          onClick={prompt.audioUrl ? () => { playAudio(); setAudioState("playing"); } : undefined}
          disabled={!prompt.audioUrl}
          title={prompt.audioUrl ? "" : "ses sonra eklenecek"}
        >
          ▶ Sesi oynat
        </button>
      )}
      {prompt.audioUrl && (
        <audio ref={audio} src={prompt.audioUrl} preload="auto" onEnded={() => setAudioState("idle")} />
      )}
    </div>
  ) : null;

  // ---- uygulayici gecidi: EKRANI ÇOCUĞA GÖSTERMEYİN -> bir kez oku -> Devam ----
  if (hasApplicator && phase === "applicator") {
    const ap = content.applicator!;
    return (
      <div className="mech applicator-mech">
        {ap.warning && <p className="tally-warn-note applicator-note">{ap.warning}</p>}
        <h2 className="prompt applicator-instruction">{ap.text}</h2>
        {ap.audioUrl && (
          <div className="center-action" style={{ margin: "6px 0 12px" }}>
            <button
              type="button"
              className="big-action-btn"
              onClick={() => { playAudio(); setAudioState("playing"); }}
            >
              {audioState === "playing" ? "▶ Çalıyor…" : "▶ Sesi dinlet"}
            </button>
            <audio ref={audio} src={ap.audioUrl} preload="auto" onEnded={() => setAudioState("idle")} />
          </div>
        )}
        <div className="center-action">
          <button
            type="button"
            className="big-action-btn"
            onClick={() => setPhase(hasReveal ? "intro" : "answer")}
          >
            {ap.buttonLabel ?? "Devam"}
          </button>
        </div>
      </div>
    );
  }

  // ---- reveal GIRIS: "aklinda tut" + Resimleri goster ----
  if (hasReveal && phase === "intro") {
    return (
      <div className="mech reveal-mech">
        <h2 className="prompt">Birazdan göreceğin resmi aklında tut.</h2>
        <hr className="q-divider" />
        <div className="center-action">
          <button
            type="button"
            className="big-action-btn"
            onClick={() => {
              setStepIx(0);
              setPhase("show");
            }}
          >
            Resimleri göster
          </button>
        </div>
      </div>
    );
  }

  // ---- reveal GOSTERIM: resimler acilir, sure sonunda kaybolur ----
  if (hasReveal && phase === "show") {
    const step = reveal!.steps![stepIx]!;
    return (
      <div className="mech reveal-mech">
        <div className="reveal-stage">
          {step.imageUrl && <img src={step.imageUrl} alt="" />}
        </div>
      </div>
    );
  }

  // ---- isitsel sayma (iconCount): DINLE-GATE + sayi/ikon (pati/el) sikli ----
  // Ses varsa: yonerge + "Sesi oynat" -> ses DALGASI -> ses bitince SIKLAR acilir.
  // Ses yoksa (iskelet): devre disi buton + siklar gorunur (kilitlenmesin).
  if (content.layout?.variant === "iconCount") {
    const showOpts = !audioGated || audioState === "done";
    return (
      <div className="mech iconcount-mech">
        {/* gate ekraninda yonerge/kural; siklar ekraninda SORU */}
        <h2 className="prompt">{showOpts ? (prompt.question ?? prompt.text) : prompt.text}</h2>
        {!showOpts && prompt.subText && <p className="prompt-sub">{prompt.subText}</p>}
        <hr className="q-divider" />
        {/* dinle-gate: ses varsa oynat -> dalga */}
        {audioGated && audioState !== "done" && (
          <div className="center-action" style={{ margin: "28px 0" }}>
            {audioState === "idle" ? (
              <button
                type="button"
                className="big-action-btn"
                onClick={() => {
                  setAudioState("playing");
                  sesiGuvenliOynat(audio.current, () => setAudioState("done"));
                }}
              >
                ▶ Sesi oynat
              </button>
            ) : (
              <div className="waveform" aria-label="ses çalıyor">
                <span /><span /><span /><span /><span /><span /><span />
              </div>
            )}
          </div>
        )}
        {/* iskelet (ses dosyasi yokken) */}
        {!audioGated && (
          <div className="center-action" style={{ margin: "18px 0" }}>
            <button type="button" className="big-action-btn" disabled title="ses sonra eklenecek">
              ▶ Sesi oynat
            </button>
          </div>
        )}
        {prompt.audioUrl && (
          <audio ref={audio} src={prompt.audioUrl} preload="auto" onEnded={() => setAudioState("done")}
            onError={() => setAudioState((s) => (s === "playing" ? "done" : s))} />
        )}
        {showOpts && (
          <div className="opts list short">
            {options.map((o) => {
              const sel = selected.includes(o.id);
              const n = o.count ?? (Number(o.text) || 0);
              return (
                <button
                  type="button"
                  key={o.id}
                  className={`opt${sel ? " sel" : ""}`}
                  onClick={() => toggle(o.id)}
                  aria-pressed={sel}
                  style={{ flexDirection: "column", gap: 8, width: 190, padding: "20px 14px" }}
                >
                  <span className="opt-text" style={{ fontSize: "2.1rem" }}>{o.text}</span>
                  {o.icon && n > 0 && (
                    <span style={{ display: "inline-flex", flexWrap: "wrap", justifyContent: "center", gap: 5, maxWidth: 184 }}>
                      {Array.from({ length: n }).map((_, i) => (
                        <CountIcon key={i} kind={o.icon!} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---- renk sirasi (colorSequence): sesli yonerge + renk kartlari SURUKLE-BIRAK ----
  // Cocuk havuzdaki renk kartini ustteki SIRA seridine surukler (duydugu sirada).
  // Seritteki karti havuza geri surukleyince cikar. Sira = cevap.
  if (content.layout?.variant === "colorSequence") {
    const showColors = !audioGated || audioState === "done";
    const poolOpts = options.filter((o) => !selected.includes(o.id));
    const dragOpt = csDrag ? options.find((o) => o.id === csDrag.id) : null;
    const startCsDrag = (e: React.PointerEvent, id: string) => {
      e.preventDefault();
      setCsDrag({ id, x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY });
    };
    return (
      <div className="mech colorseq-mech">
        {/* gate: "Sesi dikkatlice dinle." + gorevi de yaz -> ses bitince "Duyduğun renkleri sırala." */}
        <h2 className="prompt">{showColors ? (prompt.question ?? prompt.text) : prompt.text}</h2>
        {!showColors && prompt.question && <p className="prompt-sub">{prompt.question}</p>}
        {showColors && prompt.subText && <p className="prompt-sub">{prompt.subText}</p>}
        <hr className="q-divider" />
        {/* dinle-gate: ses varsa oynat -> dalga -> ses bitince renkler acilir */}
        {audioGated && audioState !== "done" && (
          <div className="center-action" style={{ margin: "24px 0" }}>
            {audioState === "idle" ? (
              <button
                type="button"
                className="big-action-btn"
                onClick={() => {
                  setAudioState("playing");
                  // ses yok/oynatilamiyor/takildiysa renkleri yine de ac
                  sesiGuvenliOynat(audio.current, () => setAudioState("done"));
                }}
              >
                ▶ Sesi oynat
              </button>
            ) : (
              <div className="waveform" aria-label="ses çalıyor">
                <span /><span /><span /><span /><span /><span /><span />
              </div>
            )}
          </div>
        )}
        {/* iskelet (ses dosyasi yokken audioButton'lu eski akis) */}
        {!audioGated && audioButtonEl}
        {audioGated && prompt.audioUrl && (
          <audio
            ref={audio}
            src={prompt.audioUrl}
            preload="auto"
            onEnded={() => setAudioState("done")}
            onError={() => setAudioState((s) => (s === "playing" ? "done" : s))}
          />
        )}
        {showColors && (
          <>
            {/* sira seridi (buraya surukle) */}
            <div className="colorseq-strip" data-cs-strip="1">
              {selected.length === 0 && (
                <span className="colorseq-hint">Renkleri buraya sırayla sürükle</span>
              )}
              {selected.map((id, i) => {
                const o = options.find((x) => x.id === id);
                return (
                  <button
                    type="button"
                    key={id}
                    className={`colorseq-btn placed${csDrag?.id === id ? " dragging" : ""}`}
                    style={{ background: o?.color ?? "#ccc" }}
                    onPointerDown={(e) => startCsDrag(e, id)}
                    aria-label={o?.text}
                  >
                    <span className="colorseq-badge">{i + 1}</span>
                  </button>
                );
              })}
            </div>
            {/* renk havuzu */}
            <div className="colorseq-pool" data-cs-pool="1">
              {poolOpts.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  className={`colorseq-btn${csDrag?.id === o.id ? " dragging" : ""}`}
                  style={{ background: o.color ?? "#ccc" }}
                  onPointerDown={(e) => startCsDrag(e, o.id)}
                  aria-label={o.text}
                />
              ))}
            </div>
          </>
        )}
        {showColors && selected.length > 0 && (
          <p className="colorseq-undo-hint">Yanlış yerleştirdiğin rengin üstüne dokunursan geri alırsın.</p>
        )}
        {/* hayalet body'ye cizilir: soru alani zoom ile kucultulunce fixed koordinatlar kayiyordu */}
        {csDrag && dragOpt && createPortal(
          <div
            className="colorseq-ghost"
            style={{ left: csDrag.x, top: csDrag.y, background: dragOpt.color ?? "#ccc" }}
          />,
          document.body
        )}
      </div>
    );
  }

  // ---- tara-isaretle (scanGrid): ustte HEDEF sekil kutusu + dagilmis oklar (buton) ----
  // Slayta sadik: hedef ustte, "aynilarini isaretle". Oklar bir cerceve icine
  // DAGINIK yerlesir; konum index tabanli sabit jitter ile hesaplanir (her
  // render'da ayni yerde kalir, ziplamaz). Oklar dondurulmez (sag/sol ayrimi bozulmasin).
  if (content.layout?.variant === "scanGrid") {
    const target = prompt.shapes?.[0];
    const n = options.length;
    const cols = 8;
    const rows = Math.max(1, Math.ceil(n / cols));
    const rnd = (i: number, s: number) => {
      const x = Math.sin(i * 12.9898 + s * 78.233) * 43758.5453;
      return x - Math.floor(x);
    };
    const padX = 5;
    const padY = 9;
    const spanX = 100 - 2 * padX;
    const spanY = 100 - 2 * padY;
    const cellX = spanX / cols;
    const cellY = spanY / rows;
    return (
      <div className="mech scangrid-mech">
        {target && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "10px 20px",
                border: "2px solid #111",
                borderRadius: 12,
              }}
            >
              <ShapeSVG shape={target} />
            </span>
          </div>
        )}
        {prompt.text && <h2 className="prompt">{prompt.text}</h2>}
        {prompt.subText && <p className="prompt-sub">{prompt.subText}</p>}
        <div
          className="scan-box"
          style={{
            position: "relative",
            width: "100%",
            height: rows * 96,
            border: "2px solid #cbd0d6",
            borderRadius: 16,
            background: "#fff",
            marginTop: 8,
          }}
        >
          {options.map((o, i) => {
            const sel = selected.includes(o.id);
            const col = i % cols;
            const row = Math.floor(i / cols);
            const jx = (rnd(i, 1) - 0.5) * cellX * 0.55;
            const jy = (rnd(i, 2) - 0.5) * cellY * 0.5;
            const left = padX + (col + 0.5) * cellX + jx;
            const top = padY + (row + 0.5) * cellY + jy;
            return (
              <button
                type="button"
                key={o.id}
                className={`shape-btn scan-btn${sel ? " sel" : ""}`}
                onClick={() => toggle(o.id)}
                aria-pressed={sel}
                style={{
                  position: "absolute",
                  left: `${left}%`,
                  top: `${top}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                {o.shape && <ShapeSVG shape={o.shape} />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- dikkat izgarasi: her sekil bir dokunmatik buton (coklu secim) ----
  if (content.layout?.variant === "shapeGrid") {
    return (
      <div className="mech shapegrid-mech">
        {prompt.text && <h2 className="prompt">{prompt.text}</h2>}
        {prompt.subText && <p className="prompt-sub">{prompt.subText}</p>}
        <div className="shape-grid">
          {options.map((o) => {
            const sel = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                className={`shape-btn${sel ? " sel" : ""}`}
                onClick={() => toggle(o.id)}
                aria-pressed={sel}
              >
                {o.shape && <ShapeSVG shape={o.shape} />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- bellek sahne eslestirme (sceneMatch): ana sahne ustte + aday sahneler buton ----
  // Cocuk, ustteki ana sahnenin tipatip aynisini secer (tekil secim). Sahneler koddan cizilir.
  if (content.layout?.variant === "sceneMatch") {
    return (
      <div className="mech scenematch-mech">
        {prompt.text && <h2 className="prompt">{prompt.text}</h2>}
        {prompt.scene && (
          <div className="scene-stimulus">
            <SceneSVG scene={prompt.scene} />
          </div>
        )}
        <hr className="q-divider" />
        {prompt.subText && <h3 className="shaperow-q">{prompt.subText}</h3>}
        <div className="opts scenematch-opts">
          {options.map((o) => {
            const sel = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                className={`opt scene-opt${sel ? " sel" : ""}`}
                onClick={() => toggle(o.id)}
                aria-pressed={sel}
              >
                {o.scene && <SceneSVG scene={o.scene} />}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- bellek duzeni (shapeRow): uyaran sekil dizisi + A/B/C aday duzenler ----
  // Her sik bir sekil satiri; tekil secim (hangi yeni duzen dogru). Sekiller
  // koddan cizilir (flat = kontursuz, slayta sadik).
  if (content.layout?.variant === "shapeRow") {
    return (
      <div className="mech shaperow-mech">
        {prompt.text && <h2 className="prompt">{prompt.text}</h2>}
        {prompt.shapes && prompt.shapes.length > 0 && (
          <div className="stimulus-row">
            {prompt.shapes.map((sh, i) => (
              <ShapeSVG key={i} shape={sh} flat />
            ))}
          </div>
        )}
        <hr className="q-divider" />
        {prompt.subText && <h3 className="shaperow-q">{prompt.subText}</h3>}
        <div className="opts shaperow-opts">
          {options.map((o) => {
            const sel = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                className={`opt shaperow-opt${sel ? " sel" : ""}`}
                onClick={() => toggle(o.id)}
                aria-pressed={sel}
              >
                <span className="shape-row">
                  {(o.shapes ?? []).map((sh, i) => (
                    <ShapeSVG key={i} shape={sh} flat />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- isitsel dizi (imageRow): her sik yan yana gorsel satiri (A/B/C) ----
  if (content.layout?.variant === "imageRow") {
    return (
      <div className="mech imagerow-mech">
        {prompt.text && <h2 className="prompt">{prompt.text}</h2>}
        <div className="opts imagerow-opts">
          {options.map((o) => {
            const sel = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                className={`opt imagerow-opt${sel ? " sel" : ""}`}
                onClick={() => toggle(o.id)}
                aria-pressed={sel}
              >
                <span className="image-row">
                  {(o.images ?? []).map((src, i) => (
                    <img key={i} src={src} alt="" draggable={false} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- okuma anlama: metin solda, soru+siklar sagda (ECUP slaydi gibi) ----
  if (prompt.story && !hasReveal && !audioGated) {
    return (
      <div className="mech reading">
        <div className="reading-passage">
          <div className="reading-head">Okuma metni</div>
          {prompt.story.split("\n\n").map((par, i) => (
            <p key={i}>{par}</p>
          ))}
        </div>
        <div className="reading-side">
          {prompt.text && <h2 className="prompt reading-q">{prompt.text}</h2>}
          <div className="opts reading-opts">
            {options.map((o, idx) => {
              const sel = selected.includes(o.id);
              return (
                <button
                  type="button"
                  key={o.id}
                  className={`opt reading-opt${sel ? " sel" : ""}`}
                  onClick={() => toggle(o.id)}
                  aria-pressed={sel}
                >
                  <span className="opt-letter">{String.fromCharCode(65 + idx)}</span>
                  <span className="opt-text">{o.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ---- cevap asamasi (normal / ses / reveal sonrasi) ----
  const withDivider = audioGated || hasReveal;
  return (
    <div
      className={`mech${isImages && options.length > 12 ? " compact" : ""}${audioGated ? " audio-gated" : ""}`}
    >
      {(prompt.text || prompt.question) && (
        <h2 className="prompt">{ml(showOptions ? (prompt.question ?? prompt.text) : prompt.text)}</h2>
      )}
      {prompt.subText && <p className="prompt-sub">{prompt.subText}</p>}
      {prompt.teacherNote && (
        <p
          style={{
            fontSize: 13,
            fontStyle: "italic",
            color: "#9aa0a6",
            opacity: 0.7,
            textAlign: "center",
            margin: "2px 0 6px",
          }}
        >
          {prompt.teacherNote}
        </p>
      )}
      {prompt.wordList && (
        <p
          className="prompt"
          style={{ fontWeight: 700, margin: "2px 0 6px", fontSize: "1.05rem" }}
        >
          {prompt.wordList}
        </p>
      )}
      {prompt.rule && (
        <div
          className="prompt-rule"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            width: "fit-content",
            maxWidth: "100%",
            margin: "8px auto 0",
            padding: "10px 16px",
            background: "#f6ecfd",
            border: "1.5px solid #820AD1",
            borderRadius: 12,
            color: "#4b2673",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          <span style={{ color: "#820AD1", fontSize: 20, lineHeight: 1.35 }}>★</span>
          <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {prompt.rule.split("·").map((line, i) => (
              <span key={i}>{line.trim()}</span>
            ))}
          </span>
        </div>
      )}
      {/* sesli soru: audioButton'lu sorularda içeriği gizlemeden çalışan "Sesi oynat" */}
      {audioButtonEl}
      {withDivider && <hr className="q-divider" />}

      {/* uyaran gorseli (reveal/ses disi) */}
      {!hasReveal && !audioGated && prompt.imageUrl && (
        <div className="stimulus">
          <img src={prompt.imageUrl} alt="" />
        </div>
      )}

      {/* uyaran gorselinin ALTINA gelen metin (sorunun devami; ör. s6) */}
      {prompt.footer && <p className="prompt-footer">{prompt.footer}</p>}

      {/* ses: idle -> oynat, playing -> dalga; ses bitince blok gider (siklar cikar). */}
      {audioGated && audioState !== "done" && (
        <div className="center-action">
          {audioState === "idle" ? (
            <button
              type="button"
              className="big-action-btn"
              onClick={() => {
                setAudioState("playing");
                sesiGuvenliOynat(audio.current, () => setAudioState("done"));
              }}
            >
              ▶ Sesi oynat
            </button>
          ) : (
            <div className="waveform" aria-label="ses çalıyor">
              <span /><span /><span /><span /><span /><span /><span />
            </div>
          )}
          <audio
            ref={audio}
            src={prompt.audioUrl}
            preload="auto"
            onEnded={() => setAudioState("done")}
            onError={() => setAudioState((s) => (s === "playing" ? "done" : s))}
          />
        </div>
      )}

      {/* siklar */}
      {showOptions && (
        <div
          className={
            isImages
              ? `opts grid${options.length > 12 ? " dense" : options.length > 4 ? " many" : ""}`
              : `opts list short${eqText ? " eq" : ""}`
          }
        >
          {options.map((o) => {
            const sel = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                className={`opt${sel ? " sel" : ""}`}
                onClick={() => toggle(o.id)}
                aria-pressed={sel}
                style={
                  content.layout?.bare
                    ? {
                        // cerceve (gri border) YOK; ama buton gorunumu icin yumusak golge + beyaz zemin.
                        border: "none",
                        background: "#fff",
                        boxShadow: sel
                          ? "0 0 0 3px #820AD1, 0 2px 8px rgba(0,0,0,0.12)"
                          : "0 2px 8px rgba(0,0,0,0.14)",
                        padding: 10,
                        borderRadius: 14,
                      }
                    : undefined
                }
              >
                {o.imageUrl ? (
                  <>
                    <img src={o.imageUrl} alt="" />
                    {o.text && <span className="opt-caption">{o.text}</span>}
                  </>
                ) : (
                  <>
                    <span className="opt-text">{o.text}</span>
                    {o.math && (
                      <span
                        style={{
                          display: "block",
                          marginTop: 8,
                          padding: "8px 14px",
                          border: "2px solid #820AD1",
                          borderRadius: 10,
                          background: "#f6ecfd",
                          color: "#4b2673",
                          fontWeight: 700,
                          whiteSpace: "pre-line",
                          textAlign: "center",
                        }}
                      >
                        {o.math}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
